import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { CustomerPreference, IdentitySession, RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { ChevronControl, Group, MarketShell, type RowValue } from "./MarketShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Settings` — CUS-03's preferences and IAM-04's sessions, which is not what `SCREEN_SPEC.md` cites.
 *
 * `docs/design/DESIGN_RECONCILIATION.md` found this before the screen was built and it is the reason
 * the measurement grades it **buildable**: _"`Settings` is buildable and `SCREEN_SPEC.md` cites
 * NTF-06 for it, which is entirely internal. CUS-03 publishes `language.locale`,
 * `communication.sms/email/push` and `experience.presentationTheme` as a typed vocabulary, and
 * IAM-04 answers `3 devices`. Every element has a source and none of them is the cited one."_
 *
 * So this screen reads CUS-03 and IAM-04 and does not touch NTF-06, whose six operations are all
 * `/internal/v1`.
 *
 * | Drawn                                   | From                                                |
 * | --------------------------------------- | --------------------------------------------------- |
 * | `Language · English`                    | CUS-03's `language.locale`                          |
 * | `Notifications · Trips, orders, offers` | CUS-03's three `communication.*` switches, and below |
 * | `Appearance · Follow device`            | CUS-03's `experience.presentationTheme`             |
 * | `Signed-in devices · 3 devices`         | IAM-04's `GET /v1/sessions?subjectId=`              |
 * | `Sign out · This device`                | the OIDC sign-out this application already performs |
 * | `Amina Kariuki`                         | the access token's own `name` claim                 |
 * | `account active`                        | **nothing reachable** — see below                   |
 *
 * ## Three things this screen says differently from the design, each for a published reason
 *
 *   - **`Trips, orders, offers` are topics; CUS-03 publishes channels.** Its enumeration is
 *     `communication.sms`, `communication.email` and `communication.push` — *how* a rider is told,
 *     not *what about*. Which topics a rider has turned off is NTF-06's, and NTF-06 is internal. So
 *     the row names the channels that are on, which is true, and the difference is flagged.
 *   - **`account active` has no reachable source.** CUS-01's profile read is addressed by a
 *     `profileId`, and nothing on this platform joins a customer identity to one — the same shape as
 *     MER-02's unlisted branches. So the chip carries the dashed `unknown` mark rather than a
 *     confident word about an account's standing.
 *   - **`Sign out · This device` is not IAM-04's revocation.** Nothing tells this browser which
 *     IAM-04 session is its own: the access token carries no session identifier and IAM-04's rows
 *     are keyed by a `deviceId` this application never sent. What it *can* do is end its own session
 *     with the issuer, which is what signing out of this device means and what
 *     `auth/authority.ts` already does. IAM-04's revocation is for the **other** devices, which is
 *     what the `Signed-in devices` row leads to — and the reconciliation records that the listing is
 *     empty today because the issuer creates sessions and IAM-04 is never told.
 */

export interface SettingsProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly customerId: string;
  /** The token's own `name` claim, which is the design's `Amina Kariuki`. */
  readonly displayName: string;
  /** Ends this device's session with the identity provider. Absent in a development build. */
  readonly onSignOut?: () => void;
  readonly onBack: () => void;
}

/** CUS-03's three communication channels, in the order its own enumeration lists them. */
const CHANNELS: readonly { readonly key: string; readonly word: string }[] = [
  { key: "communication.sms", word: "SMS" },
  { key: "communication.email", word: "email" },
  { key: "communication.push", word: "push" },
];

interface Loaded {
  readonly preferences: readonly CustomerPreference[];
  /** `undefined` when IAM-04 refused or failed, which is not the same as zero devices. */
  readonly sessions: readonly IdentitySession[] | undefined;
}

export function Settings(props: SettingsProps): ReactNode {
  const { api, customerId } = props;

  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    setState({ kind: "loading" });
    Promise.all([
      api.preferences(customerId),
      /*
       * Soft, and the distinction matters on this screen more than elsewhere.
       *
       * A rider whose preferences could not be read has no settings screen. A rider whose **session
       * list** could not be read has four rows and one absence — and today that read answers an
       * empty list for everybody, because the issuer creates the session and IAM-04 is never told.
       * `undefined` here means "could not read" and an empty array means "read, and there are none";
       * the row draws those two differently, because telling somebody with three devices that they
       * have none is the fault the console's own sign-out dialog was changed to avoid.
       */
      api.sessions(customerId).catch(() => undefined),
    ])
      .then(([preferences, sessions]) => {
        setLoaded({ preferences, sessions });
        setState({ kind: "ready" });
      })
      .catch((error: unknown) =>
        setState(stateForError(error, [SCOPES.preferences, SCOPES.sessionRead])),
      );
  }, [api, customerId]);

  useEffect(load, [load]);

  if (state.kind !== "ready" || loaded === undefined) {
    return (
      <MarketState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading your preferences."
        barTitle="Settings"
        barSub={props.displayName}
      />
    );
  }

  const value = (key: string): CustomerPreference | undefined =>
    loaded.preferences.find((preference) => preference.key === key);

  const language = value("language.locale");
  const appearance = value("experience.presentationTheme");

  const preferenceRows: RowValue[] = [
    {
      id: "language",
      lead: "translate",
      title: "Language",
      ...(language === undefined
        ? { subAbsent: "CUS-03 holds no language.locale for this customer" }
        : { sub: String(language.value) }),
      control: <ChevronControl label="Change language" unavailable={CHANGE_IS_A_PUT} />,
    },
    {
      id: "notifications",
      lead: "notifications",
      title: "Notifications",
      sub: channelSentence(loaded.preferences),
      control: <ChevronControl label="Change notifications" unavailable={CHANGE_IS_A_PUT} />,
    },
    {
      id: "appearance",
      lead: "contrast",
      title: "Appearance",
      ...(appearance === undefined
        ? { subAbsent: "CUS-03 holds no experience.presentationTheme for this customer" }
        : { sub: String(appearance.value) }),
      control: <ChevronControl label="Change appearance" unavailable={CHANGE_IS_A_PUT} />,
    },
    {
      id: "devices",
      lead: "devices",
      title: "Signed-in devices",
      ...devicesLine(loaded.sessions),
      control: (
        <ChevronControl
          label="See signed-in devices"
          unavailable="No design file draws a device list screen for this channel, so there is nowhere published to go"
        />
      ),
    },
  ];

  const signOutRow: RowValue = {
    id: "sign-out",
    lead: "logout",
    title: "Sign out",
    sub: "This device",
    control: (
      <ChevronControl
        label="Sign out of this device"
        {...(props.onSignOut === undefined
          ? {
              unavailable:
                "This build has no identity provider in the loop, so there is no session with one to end",
            }
          : { onPress: props.onSignOut })}
      />
    ),
  };

  return (
    <MarketShell
      screen="settings"
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle="Settings"
      barSub={props.displayName}
      right={{
        glyph: "help",
        label: "Help",
        unavailable: "No design file draws a help sheet for this screen",
      }}
      badge={{ kind: "unknown", label: "account standing not published" }}
      title="Preferences"
      sub="Language, notifications and devices"
    >
      <Group rows={preferenceRows} />
      <Group rows={[signOutRow]} />
    </MarketShell>
  );
}

/**
 * Why none of the four chevrons leads anywhere.
 *
 * CUS-03's write is `PUT /v1/customers/{customerId}/preferences/{key}` — one key at a time, and it
 * is published and idempotent. What is **not** published is a screen: the bundle draws thirty-two
 * rider screens and none of them is a language picker, a notification sheet or an appearance
 * chooser. The design's `›` says "there is more behind this", and building what is behind it would
 * be designing three screens rather than copying one. So the row is drawn, the chevron is drawn, and
 * pressing it says so.
 */
const CHANGE_IS_A_PUT =
  "PUT /v1/customers/{customerId}/preferences/{key} (CUS-03) is published; no design file draws the screen behind this chevron";

/**
 * `Trips, orders, offers` in the design; the channels that are on, here.
 *
 * CUS-03's `value` is typed `string or boolean`, so a channel's value is read as a boolean and
 * anything else — a tenant default stored as the string `"true"`, say — is treated as not a plain
 * yes. A channel CUS-03 holds no row for is not drawn as off: an absent preference means the tenant
 * default applies, and `GET …/preferences/effective` is the operation that resolves one. This screen
 * reads the customer's own rows, so an absent channel is said as absent.
 */
export function channelSentence(preferences: readonly CustomerPreference[]): string {
  const on: string[] = [];
  const unset: string[] = [];
  for (const channel of CHANNELS) {
    const found = preferences.find((preference) => preference.key === channel.key);
    if (found === undefined) unset.push(channel.word);
    else if (found.value === true) on.push(channel.word);
  }
  if (on.length === 0 && unset.length === CHANNELS.length) return "No channel is set for you";
  const words = on.length === 0 ? "No channel is on" : on.join(", ");
  return unset.length === 0 ? words : `${words} · ${unset.join(", ")} not set`;
}

/**
 * `3 devices`, or the reason there is no count.
 *
 * Three cases and not two. A failed read is an absence; an empty list is `No other device`, which is
 * what a rider who has signed in once should see; a list is its own length. The reconciliation
 * records that the listing is empty for everybody today, because the issuer creates the session and
 * IAM-04 is never told — so this row is currently the honest second case rather than the third, and
 * it will become the third without this file changing.
 */
function devicesLine(
  sessions: readonly IdentitySession[] | undefined,
): { readonly sub: string } | { readonly subAbsent: string } {
  if (sessions === undefined) {
    return { subAbsent: "IAM-04's session list could not be read" };
  }
  const active = sessions.filter((session) => session.status === "active").length;
  if (active === 0) return { sub: "No device recorded" };
  return { sub: `${String(active)} ${active === 1 ? "device" : "devices"}` };
}
